/**
 * Schema Migration Utility for Google Drive JSONs
 *
 * Every JSON persisted in Drive MUST carry a schemaVersion field.
 * This module centralises version stamping (writes) and migration (reads).
 *
 * v1 — initial version (21/mar/2026): adds schemaVersion: 1 to all files.
 * v6 — Fase Empírica v4 (09/abr/2026): empiricalCodebook in project.json,
 *      includedInComparacao on highlights (DN8, DN25). See case 5 below.
 * v7 — IA situada na leitura (Sprint 009, jun/2026): campo aditivo
 *      `savedExplanation` no Highlight. Puramente aditivo — nenhum dado é
 *      transformado (highlights v6 sem o campo carregam intactos). See case 6.
 * Future versions: add cases to the switch inside migrateIfNeeded.
 */

import { logger } from '../../utils/logger';
import { migrateTagsV1toV2 } from '../../constants/semantic-tags';

// ── Current version ─────────────────────────────────────────────
export const CURRENT_SCHEMA_VERSION = 7;

// ── File type registry (for logging / future per-type migrations) ──
export type DriveFileType =
  | 'referencias'
  | 'collections'
  | 'highlights'
  | 'repositoryItems'
  | 'repositoryNucleos'
  | 'repositoryNotes'
  | 'userProfile'
  | 'project'
  | 'scratchpadNotes'
  | 'namingConfig'
  | 'readingOrder'
  | 'usageMap'
  | 'crossings'
  | 'subscription'
  | 'destino'
  | 'summaryDocument'
  | 'sinteses'
  | 'preferences'
  | 'conversations';

// ── v3→v4: savedAnnotation → savedComments ──────────────────────
function migrateCommentsV3toV4(record: Record<string, unknown>, fileType: DriveFileType): void {
  if (fileType !== 'highlights') return;
  const highlights = record.highlights;
  if (!Array.isArray(highlights)) return;
  for (const h of highlights) {
    if (h.savedComments && h.savedComments.length > 0) continue;
    if (h.savedAnnotation && typeof h.savedAnnotation === 'string') {
      const translationMarker = '📝 Tradução:\n';
      const markerIdx = h.savedAnnotation.indexOf(translationMarker);
      let comment: string;
      let embeddedTranslation: string | undefined;
      if (markerIdx !== -1) {
        comment = h.savedAnnotation.substring(0, markerIdx).trim();
        embeddedTranslation = h.savedAnnotation.substring(markerIdx + translationMarker.length).trim();
      } else {
        comment = h.savedAnnotation.trim();
      }
      if (comment) h.savedComments = [comment];
      if (embeddedTranslation && !h.savedTranslation) h.savedTranslation = embeddedTranslation;
      delete h.savedAnnotation;
    }
  }
}

// ── v4→v5: ensure empiricalTags exists on every highlight ───────
function migrateEmpiricalTagsV4toV5(record: Record<string, unknown>, fileType: DriveFileType): boolean {
  if (fileType !== 'highlights') return false;
  const highlights = record.highlights;
  if (!Array.isArray(highlights)) return false;
  let changed = false;
  for (const h of highlights) {
    if (!Array.isArray(h.empiricalTags)) {
      h.empiricalTags = [];
      changed = true;
    }
  }
  return changed;
}

// ── v5→v6 (Fase Empírica v4 — DN8): init empiricalCodebook em project.json ──
//
// Inicializa um codebook vazio se ausente. Cross-file auto-populate (a partir
// de labels antigos dos highlights) é responsabilidade da função async
// `migrateProjectV5toV6Codebook` em `projects.ts` — chamada explicitamente do
// load path, não pelo framework síncrono.
function migrateProjectV5toV6(record: Record<string, unknown>, fileType: DriveFileType): boolean {
  if (fileType !== 'project') return false;
  if (record.empiricalCodebook === undefined || record.empiricalCodebook === null) {
    record.empiricalCodebook = { columns: [], categories: [] };
    return true;
  }
  return false;
}

// ── v5→v6 (Fase Empírica v4 — DN25): seta includedInComparacao em highlights ──
//
// Pilar 1 crítico: trechos que JÁ tinham coluna ou categorias (legacy codificado)
// são presumidos como "no quadro" para preservar trabalho do beta tester. Default
// `false` para destaques sem coluna nem categorias.
//
// Conversão labels→IDs em `empiricalTags` é cross-file (precisa do codebook do
// project.json) e fica em `convertHighlightLabelsToIds` em `repository.ts` —
// chamada explicitamente do load path quando codebook estiver disponível.
function migrateHighlightsV5toV6(record: Record<string, unknown>, fileType: DriveFileType): boolean {
  if (fileType !== 'highlights') return false;
  const highlights = record.highlights;
  if (!Array.isArray(highlights)) return false;
  let changed = false;
  for (const h of highlights) {
    if (h.includedInComparacao === undefined || h.includedInComparacao === null) {
      const hasColumn = typeof h.empiricalColumn === 'string' && h.empiricalColumn.length > 0;
      const hasCategories = Array.isArray(h.empiricalTags) && h.empiricalTags.length > 0;
      h.includedInComparacao = hasColumn || hasCategories;
      changed = true;
    }
  }
  return changed;
}

// ── Single-object migration (5 guards — V6 spec) ───────────────
/**
 * Ensures `data` has the current schemaVersion.
 * Returns { data, migrated } where migrated=true means data was transformed.
 *
 * FAIL-SAFE: if anything throws, returns original data unchanged.
 */
export function migrateIfNeeded<T>(
  data: T,
  fileType: DriveFileType,
): { data: T & { schemaVersion: number }; migrated: boolean } {
  try {
    // ═══ GUARD 1 (C22): Normalizar formato legado ═══
    // PdfReaderPage antigo salvava highlights como array puro.
    if (fileType === 'highlights' && Array.isArray(data)) {
      data = { highlights: data } as unknown as T;
    }

    const record = data as Record<string, unknown>;

    // ═══ GUARD 2 (C31): Validar schemaVersion como number ═══
    const rawVersion = record.schemaVersion;
    const currentVersion: number | undefined =
      typeof rawVersion === 'number' ? rawVersion : undefined;

    // ═══ GUARD 3 (C21): Não tocar dados de versão futura ═══
    if (currentVersion !== undefined && currentVersion > CURRENT_SCHEMA_VERSION) {
      return { data: data as T & { schemaVersion: number }, migrated: false };
    }

    // ═══ GUARD 4: Já na versão atual ═══
    if (currentVersion === CURRENT_SCHEMA_VERSION) {
      return { data: data as T & { schemaVersion: number }, migrated: false };
    }

    let migrated = false;

    // ═══ GUARD 5 (C20): case undefined para dados pre-schema ═══
    switch (currentVersion) {
      case undefined:  // dados pre-schema (antes do sistema de versão)
      case 1: {        // schema v1 → v2
        if (fileType === 'highlights') {
          const highlights = (record as Record<string, unknown>).highlights;
          if (Array.isArray(highlights)) {
            for (const highlight of highlights) {
              if (highlight.contentTags && highlight.contentTags.length > 0) {
                const migration = migrateTagsV1toV2(highlight.contentTags);
                if (migration.changed) {
                  highlight._migrationMeta = {
                    migratedAt: new Date().toISOString(),
                    fromTags: [...highlight.contentTags],
                    toTags: migration.tags,
                    fromSchemaVersion: currentVersion ?? 0,
                  };
                  highlight.contentTags = migration.tags;
                  highlight.color = migration.color;
                  migrated = true;
                }
              }
            }
          }
        }

        // Version bump always counts as migration (stamp to v2)
        migrated = true;
        migrateEmpiricalTagsV4toV5(record, fileType);
        // v5→v6 cascading (Fase Empírica v4)
        if (migrateProjectV5toV6(record, fileType)) migrated = true;
        if (migrateHighlightsV5toV6(record, fileType)) migrated = true;
        break;
      }
      case 2: {
        // v2 → v3: additive fields only (shapeType, stickyNote on Highlight)
        // No data transformation needed — new fields are optional
        // Then apply v3→v4, v4→v5 e v5→v6 migrations as well
        migrated = true;
        migrateCommentsV3toV4(record, fileType);
        migrateEmpiricalTagsV4toV5(record, fileType);
        if (migrateProjectV5toV6(record, fileType)) migrated = true;
        if (migrateHighlightsV5toV6(record, fileType)) migrated = true;
        break;
      }
      case 3: {
        // v3 → v4: migrate savedAnnotation → savedComments
        // Then apply v4→v5 e v5→v6 migrations as well
        migrateCommentsV3toV4(record, fileType);
        migrateEmpiricalTagsV4toV5(record, fileType);
        if (migrateProjectV5toV6(record, fileType)) migrated = true;
        if (migrateHighlightsV5toV6(record, fileType)) migrated = true;
        migrated = true;
        break;
      }
      case 4: {
        // v4 → v5: ensure empiricalTags exists on every highlight
        if (migrateEmpiricalTagsV4toV5(record, fileType)) {
          migrated = true;
        }
        // v5 → v6: aplica também as migrations de v5→v6 (cascading)
        if (migrateProjectV5toV6(record, fileType)) migrated = true;
        if (migrateHighlightsV5toV6(record, fileType)) migrated = true;
        migrated = true;
        break;
      }
      case 5: {
        // v5 → v6 (Fase Empírica v4):
        // - 'project': init empiricalCodebook se ausente (DN8)
        // - 'highlights': seta includedInComparacao (DN25 — Pilar 1 — legacy codificado entra no quadro)
        if (migrateProjectV5toV6(record, fileType)) migrated = true;
        if (migrateHighlightsV5toV6(record, fileType)) migrated = true;
        migrated = true;
        break;
      }
      case 6: {
        // v6 → v7 (IA situada na leitura — Sprint 009):
        // campo aditivo `savedExplanation` no Highlight. Puramente aditivo —
        // sem transformação de dados (precedente: v2→v3). Só o stamp sobe.
        migrated = true;
        break;
      }
    }

    const oldLabel = currentVersion === undefined || currentVersion === null ? 'none' : `v${currentVersion}`;
    logger.info('[SchemaMigration]', `Migrated ${fileType} from ${oldLabel} to v${CURRENT_SCHEMA_VERSION}${migrated ? ' (data changed)' : ' (version stamp only)'}`);

    return {
      data: { ...(data as object), schemaVersion: CURRENT_SCHEMA_VERSION } as T & { schemaVersion: number },
      migrated,
    };
  } catch {
    // Fail-safe: never reject data
    return { data: data as T & { schemaVersion: number }, migrated: false };
  }
}

// ── Array migration (Work[], Collection[], etc.) ────────────────
/**
 * Applies migrateIfNeeded to every item in an array.
 * Returns migrated=true if ANY item was migrated.
 */
export function migrateArrayIfNeeded<T>(
  items: T[],
  fileType: DriveFileType,
): { data: (T & { schemaVersion: number })[]; migrated: boolean } {
  let anyMigrated = false;
  const result = items.map(item => {
    const { data, migrated } = migrateIfNeeded(item, fileType);
    if (migrated) anyMigrated = true;
    return data;
  });
  return { data: result, migrated: anyMigrated };
}

// ── Write helpers (stamp before saving) ─────────────────────────
/**
 * Stamps schemaVersion on a single object before writing to Drive.
 */
export function stampSchema<T>(data: T): T & { schemaVersion: number } {
  return { ...(data as object), schemaVersion: CURRENT_SCHEMA_VERSION } as T & { schemaVersion: number };
}

/**
 * Stamps schemaVersion on every item in an array before writing to Drive.
 */
export function stampSchemaArray<T>(items: T[]): (T & { schemaVersion: number })[] {
  return items.map(item => stampSchema(item));
}
